import { ApiClient, type FetchLike } from "./api-client.js";
import { AuthController } from "./auth-controller.js";
import { DomAuthView, resolveAuthElements } from "./auth-view.js";
import { readApiBaseUrl, readGoogleClientId } from "./config.js";
import { ContactController } from "./contact-controller.js";
import { DomContactView, resolveContactElements } from "./contact-view.js";
import { PromptController } from "./controller.js";
import {
  GsiGoogleSignIn,
  type GoogleSignIn,
  type GsiWindow,
} from "./google-sign-in.js";
import { DomView, resolveElements } from "./view.js";

export type StartOptions = {
  readonly document: Document;
  readonly window: GsiWindow;
  readonly fetch: FetchLike;
  /** Overridden in tests; production uses the real Google library. */
  readonly googleSignIn?: GoogleSignIn;
};

export type StartedApp = {
  readonly prompt: PromptController;
  readonly contact: ContactController;
  readonly auth: AuthController;
};

/**
 * Composition root for the client.
 *
 * The prompt and contact controllers are wired up unconditionally even though
 * their forms start hidden: their elements are in the document either way, and
 * building them lazily after sign-in would add ordering with nothing to gain.
 */
export async function start(options: StartOptions): Promise<StartedApp> {
  const api = new ApiClient({
    fetch: options.fetch,
    baseUrl: readApiBaseUrl(options.document),
  });

  const auth = new AuthController({
    view: new DomAuthView(resolveAuthElements(options.document)),
    api,
    googleSignIn:
      options.googleSignIn ??
      new GsiGoogleSignIn(
        options.window,
        options.document,
        readGoogleClientId(options.document),
      ),
  });

  // A cookie that expires mid-session must return the whole page to signed out,
  // not just fail the one request that noticed.
  api.onUnauthenticated(() => {
    auth.handleUnauthenticated();
  });

  const prompt = new PromptController({
    view: new DomView(resolveElements(options.document)),
    api,
  });
  const contact = new ContactController({
    view: new DomContactView(resolveContactElements(options.document)),
    api,
  });

  prompt.start();
  contact.start();
  await auth.start();

  return { prompt, contact, auth };
}
